import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Pagination,
  Skeleton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Avatar,
  Divider,
  Link as MuiLink,
  Tooltip,
} from '@mui/material';
import {
  Search,
  FileDownload,
  Refresh,
  WhatsApp,
  Phone,
  Email,
  Close as CloseIcon,
  Person,
  School,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  courseLeadService,
  CourseLead,
  CourseLeadStatus,
  CourseLeadCourseRef,
  CourseLeadUserRef,
  CourseLeadBusinessRef,
} from '@/services/business/courseLeadService';
import moment from 'moment';

interface Props {
  businessId: string;
}

const STATUS_OPTIONS: { value: CourseLeadStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_COLOR: Record<CourseLeadStatus, 'default' | 'primary' | 'success' | 'warning' | 'info' | 'error' | 'secondary'> = {
  new: 'info',
  contacted: 'primary',
  in_progress: 'secondary',
  qualified: 'warning',
  converted: 'success',
  not_interested: 'default',
  closed: 'error',
};

const DAYS_OPTIONS = [
  { value: 0, label: 'All time' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const getCourseRef = (l: CourseLead): CourseLeadCourseRef | null => {
  if (!l.businessCourseId) return null;
  if (typeof l.businessCourseId === 'string') return { _id: l.businessCourseId };
  return l.businessCourseId;
};

const getBusinessRef = (l: CourseLead): CourseLeadBusinessRef | null => {
  if (!l.businessId) return null;
  if (typeof l.businessId === 'string') return { _id: l.businessId };
  return l.businessId;
};

const getUserRef = (l: CourseLead): CourseLeadUserRef | null => {
  if (!l.userId) return null;
  if (typeof l.userId === 'string') return { _id: l.userId };
  return l.userId;
};

export const CourseLeadsList: React.FC<Props> = ({ businessId }) => {
  const theme = useTheme();

  const [leads, setLeads] = useState<CourseLead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState<CourseLeadStatus | 'all'>('all');
  const [days, setDays] = useState<number>(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeLead, setActiveLead] = useState<CourseLead | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const limit = 10;

  const loadLeads = async () => {
    try {
      setIsLoading(true);
      const response = await courseLeadService.myLeads({
        businessId,
        page,
        limit,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined,
        days: days > 0 ? days : undefined,
      });
      setLeads(response.data.courseLeads);
      setTotalPages(response.data.pagination.pages || 1);
      setTotalItems(response.data.pagination.total || 0);
    } catch {
      // toast handled by apiClient
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, days, search, businessId]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { blob, filename } = await courseLeadService.exportExcel({
        businessId,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        days: days > 0 ? days : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // toast handled
    } finally {
      setIsExporting(false);
    }
  };

  const openDetail = async (id: string) => {
    setActiveId(id);
    try {
      setIsLoadingDetail(true);
      const response = await courseLeadService.getById(id);
      setActiveLead(response.data);
    } catch {
      setActiveId(null);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setActiveId(null);
    setActiveLead(null);
    setCommentText('');
  };

  const handleStatusChange = async (status: CourseLeadStatus) => {
    if (!activeLead) return;
    try {
      setIsUpdatingStatus(true);
      await courseLeadService.updateStatus(activeLead._id, status);
      setActiveLead({ ...activeLead, status });
      setLeads((prev) => prev.map((l) => (l._id === activeLead._id ? { ...l, status } : l)));
      toast.success('Status updated');
    } catch {
      // toast handled
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!activeLead || !commentText.trim()) return;
    try {
      setIsPostingComment(true);
      const response = await courseLeadService.addComment(activeLead._id, commentText.trim());
      setActiveLead(response.data);
      setCommentText('');
    } catch {
      // toast handled
    } finally {
      setIsPostingComment(false);
    }
  };

  const cardSx = {
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'primary.main',
      boxShadow: theme.shadows[3],
      transform: 'translateY(-2px)',
    },
  };

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.25,
    borderRadius: 5,
    bgcolor: 'action.hover',
    fontSize: '0.72rem',
    color: 'text.secondary',
  };

  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    leads.forEach((l) => { counts[l.status] = (counts[l.status] || 0) + 1; });
    return counts;
  }, [leads]);

  return (
    <Box>
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 3,
          alignItems: 'center',
        }}
      >
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ flex: 1, minWidth: 220 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, email, mobile, course title…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <Search sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />,
                endAdornment: searchInput ? (
                  <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null,
              },
            }}
          />
        </Box>
        <TextField
          size="small"
          select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as CourseLeadStatus | 'all'); setPage(1); }}
          sx={{ minWidth: 160 }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          value={days}
          onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 160 }}
        >
          {DAYS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={loadLeads} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExport}
          disabled={isExporting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isExporting ? 'Exporting…' : 'Export Excel'}
        </Button>
      </Box>

      {/* Stats summary */}
      {!isLoading && leads.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {Object.entries(stats).map(([status, count]) => (
            <Chip
              key={status}
              size="small"
              label={`${status.replace(/_/g, ' ')}: ${count}`}
              color={STATUS_COLOR[status as CourseLeadStatus] || 'default'}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          ))}
        </Box>
      )}

      {/* Leads list */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : leads.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <School sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No course leads yet
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Course enquiries from interested learners will appear here.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {leads.map((lead) => {
            const courseRef = getCourseRef(lead);
            const userRef = getUserRef(lead);
            return (
              <Box key={lead._id} onClick={() => openDetail(lead._id)} sx={{ ...cardSx, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontWeight: 700, flexShrink: 0 }}>
                    {lead.name?.charAt(0).toUpperCase() || <Person />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {lead.name}
                      </Typography>
                      <Chip
                        label={lead.status.replace(/_/g, ' ')}
                        size="small"
                        color={STATUS_COLOR[lead.status]}
                        sx={{ textTransform: 'capitalize', fontWeight: 600, height: 20, fontSize: '0.68rem' }}
                      />
                      {courseRef?.level && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={courseRef.level}
                          color="primary"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                      {courseRef?.courseType && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={courseRef.courseType}
                          color={courseRef.courseType === 'Paid' ? 'secondary' : 'success'}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.75 }}>
                      Interested in <strong style={{ color: theme.palette.text.primary }}>{lead.courseTitle || courseRef?.courseTitle || 'this course'}</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {lead.mobileNumber && (
                        <Box sx={pillSx}>
                          <Phone sx={{ fontSize: 13 }} /> {lead.mobileNumber}
                        </Box>
                      )}
                      {lead.email && (
                        <Box sx={{ ...pillSx, maxWidth: 240, overflow: 'hidden' }}>
                          <Email sx={{ fontSize: 13 }} />
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.email}</Box>
                        </Box>
                      )}
                      {lead.location?.city && (
                        <Box sx={pillSx}>
                          {lead.location.city}{lead.location.state ? `, ${lead.location.state}` : ''}
                        </Box>
                      )}
                      {userRef?.userId && (
                        <Box sx={pillSx}>
                          @{userRef.userId}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
                      {moment(lead.createdAt).fromNow()}
                    </Typography>
                    {lead.comments?.length > 0 && (
                      <Typography variant="caption" color="text.disabled">
                        {lead.comments.length} comment{lead.comments.length === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {leads.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{(page - 1) * limit + 1}</strong>–<strong>{Math.min(page * limit, totalItems)}</strong> of <strong>{totalItems}</strong> {totalItems === 1 ? 'lead' : 'leads'}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!activeId} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Course Lead Details</Typography>
          <IconButton size="small" onClick={closeDetail}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingDetail || !activeLead ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, fontWeight: 700 }}>
                  {activeLead.name?.charAt(0).toUpperCase() || <Person />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>{activeLead.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submitted {moment(activeLead.createdAt).format('DD MMM YYYY, h:mm A')}
                  </Typography>
                </Box>
              </Box>

              {/* Contact rows */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeLead.mobileNumber && (
                  <Button
                    component="a"
                    href={`tel:${activeLead.mobileNumber}`}
                    size="small"
                    variant="outlined"
                    startIcon={<Phone />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {activeLead.mobileNumber}
                  </Button>
                )}
                {activeLead.whatsappNumber && (
                  <Button
                    component="a"
                    href={`https://wa.me/91${activeLead.whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<WhatsApp />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    WhatsApp
                  </Button>
                )}
                {activeLead.email && (
                  <Button
                    component="a"
                    href={`mailto:${activeLead.email}`}
                    size="small"
                    variant="outlined"
                    startIcon={<Email />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Email
                  </Button>
                )}
              </Box>

              {/* Course info */}
              {(() => {
                const courseRef = getCourseRef(activeLead);
                const businessRef = getBusinessRef(activeLead);
                const finalPrice = courseRef && courseRef.price && courseRef.discount
                  ? courseRef.price - (courseRef.price * (courseRef.discount as number)) / 100
                  : courseRef?.price;
                return (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, mb: 0.5 }}>
                      Course
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {activeLead.courseTitle || courseRef?.courseTitle || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {courseRef?.level && (
                        <Chip size="small" variant="outlined" label={courseRef.level} color="primary" sx={{ height: 22, fontSize: '0.7rem' }} />
                      )}
                      {courseRef?.courseType && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={courseRef.courseType}
                          color={courseRef.courseType === 'Paid' ? 'secondary' : 'success'}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    {courseRef?.courseType === 'Paid' && courseRef?.price ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 0.75 }}>
                        <Typography variant="body2" color="primary" fontWeight={700}>
                          ₹{Number(finalPrice).toLocaleString('en-IN')}
                        </Typography>
                        {courseRef.discount ? (
                          <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                            ₹{courseRef.price.toLocaleString('en-IN')}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                    {businessRef?.businessName && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {businessRef.businessName}
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              {/* Profile info */}
              {(() => {
                const userRef = getUserRef(activeLead);
                const profileId = userRef?._id;
                if (!activeLead.location?.city && !profileId) return null;
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {activeLead.location?.city && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600 }}>
                          Location
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {activeLead.location.city}{activeLead.location.state ? `, ${activeLead.location.state}` : ''}
                        </Typography>
                      </Box>
                    )}
                    {profileId && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600 }}>
                          Profile
                        </Typography>
                        <MuiLink href={`/profile/${profileId}`} target="_blank" rel="noopener noreferrer" variant="body2" fontWeight={500}>
                          View profile
                        </MuiLink>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              <Divider />

              {/* Status update */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, mb: 1 }}>
                  Update Status
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={activeLead.status}
                  onChange={(e) => handleStatusChange(e.target.value as CourseLeadStatus)}
                  disabled={isUpdatingStatus}
                >
                  {STATUS_OPTIONS.filter((s) => s.value !== 'all').map((opt) => (
                    <MenuItem key={opt.value} value={opt.value as CourseLeadStatus}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider />

              {/* Comments */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, mb: 1 }}>
                  Notes & Comments ({activeLead.comments?.length || 0})
                </Typography>
                {(activeLead.comments || []).length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5, maxHeight: 220, overflowY: 'auto' }}>
                    {activeLead.comments.map((c) => (
                      <Box
                        key={c._id}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'action.hover',
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {c.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {c.addedBy?.name || 'You'} · {moment(c.createdAt).fromNow()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={4}
                    placeholder="Add a note (e.g., called the lead, will follow up tomorrow)…"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    disabled={isPostingComment}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!commentText.trim() || isPostingComment}
                    variant="contained"
                    sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start' }}
                  >
                    {isPostingComment ? 'Adding…' : 'Add'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
